import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { SITE_URL } from "@/lib/seo";

/** The eight FAQ pairs, verbatim. Shared by the UI and the FAQPage JSON-LD. */
export const FAQS = [
  {
    q: "What does an AI Opportunity Assessment actually produce?",
    a: "A ranked map of where AI can create value in your specific business, with an estimate of impact, effort, and time to payback for each opportunity, plus a recommended sequence. It's a working document you can act on — with us or without us.",
  },
  {
    q: "We're a small business. Is AI worth it at our size?",
    a: "Often more so, because small teams feel repetitive work harder. The honest answer depends on your operations, which is what the assessment is for. Sometimes the answer is that a process fix beats an AI project, and we'll say that.",
  },
  {
    q: "Do we need clean, organised data before any of this works?",
    a: "Not always. Some of the highest-return opportunities involve no historical data at all. Where data quality does matter, we'll tell you what's needed before you spend anything on building.",
  },
  {
    q: "Will this replace people on my team?",
    a: "That isn't what we sell. Most of what we find removes repetitive work from people who have better things to do. If headcount reduction is your goal, say so early — it changes what we'd recommend and whether we're the right firm.",
  },
  {
    q: "How much does it cost?",
    a: "The first call is free. Assessments and builds are fixed-quote, scoped in writing before any work starts.",
  },
  {
    q: "How long before something is actually working?",
    a: "The assessment is measured in days, not months. Build timelines depend entirely on what we find — we commit to a date in the written scope rather than guessing here.",
  },
  {
    q: "What if AI isn't the right answer for us?",
    a: "Then we tell you on the call, before you've paid anything. A consultancy that finds an AI opportunity in every business isn't looking very hard.",
  },
  {
    q: "Do you still do business funding work?",
    a: "Yes. SBA loans, business plans, and grant applications are a separate practice we continue to run — useful whether you're financing an AI investment or raising capital for something else.",
  },
] as const;

export const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SITE_URL}/#faqpage`,
  url: SITE_URL,
  mainEntity: FAQS.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
};

export function Faq() {
  return (
    <Section tone="paper">
      <Container variant="narrow">
        <Reveal className="max-w-[52ch]">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-3 font-display text-h2 text-ink-900">
            The things people ask on the first call.
          </h2>
        </Reveal>

        <Reveal className="mt-10">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((faq, i) => (
              <AccordionItem key={faq.q} value={`item-${i + 1}`} className="border-ink-200">
                <AccordionTrigger className="text-ink-900">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-ink-600">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Container>
    </Section>
  );
}

export default Faq;
